// Shared subscription feature data used by both DatabaseTool and SubscriptionSettings.
// Keep PLAN_DEFAULTS in sync with SubscriptionTierDefaults.cs in velaro-messaging.

// ── AI Quota numeric defaults by plan ────────────────────────────────────────
// These are reference values shown in the UI. The authoritative limits live in
// PackageVersion.cs / SubscriptionTierDefaults.cs in velaro-messaging.
export const AI_QUOTA_DEFAULTS: Record<string, {
  MaxAiConversationsPerMonth: number;
  EnableAiOverage: boolean;
  AiOveragePricePerConversation: number;
  EnableByokAi: boolean;
  MaxMcpServers: number;
  MaxMcpAgentCallsPerMonth: number;
}> = {
  basic: {
    MaxAiConversationsPerMonth: 500,
    EnableAiOverage: true,
    AiOveragePricePerConversation: 0.08,
    EnableByokAi: false,
    MaxMcpServers: 0,
    MaxMcpAgentCallsPerMonth: 0,
  },
  pro: {
    MaxAiConversationsPerMonth: 2000,
    EnableAiOverage: true,
    AiOveragePricePerConversation: 0.06,
    EnableByokAi: false,
    MaxMcpServers: 3,
    MaxMcpAgentCallsPerMonth: 1000,
  },
  enterprise: {
    MaxAiConversationsPerMonth: 10000,
    EnableAiOverage: true,
    AiOveragePricePerConversation: 0.04,
    EnableByokAi: true,
    MaxMcpServers: 50,
    MaxMcpAgentCallsPerMonth: 10000,
  },
};

export const PLAN_DEFAULTS: Record<string, Record<string, boolean>> = {
  basic: {
    // Channels
    EnableWeb: true, EnableSms: false, EnableEmail: false, EnableWhatsapp: false,
    EnableFacebook: false, EnableInstagram: false, EnableTikTok: false, EnableAppleMessages: false,
    EnableRcs: false, EnableRcsRichMessages: false,
    // AI & Automation
    EnableAI: true, EnableWorkflowRules: true, EnableAutomation: true,
    EnableMcp: false, EnableQuickStartWizards: true, EnableBotVoice: false,
    EnableOrchestration: false, EnableAvatarMode: false,
    EnableBotEmailSkill: false, EnableBotSmsSkill: false,
    EnableAdvancedAiSkills: false, EnableDestructiveAiSkills: false,
    EnableAiOverage: true, EnableByokAi: false,
    // Knowledge Base
    EnableKnowledgeBase: true, EnableKbBotIndexing: true,
    EnableSemanticKbSearch: false, EnableConversationTraining: false,
    EnableScraperBotIndexing: false, EnableYoutubeBotIndexing: false,
    EnableFileIngestion: false, EnableOneDriveIngestionSync: false,
    EnableJsonImport: false, EnableScraperAutoRebuild: false,
    // Routing
    EnableAutoRouting: false, EnableTierEscalation: false, EnablePriorityQueue: false, EnableVideoChat: false,
    // Agent Features
    EnableCollisionDetection: false, EnableAgentAssist: false, EnableAgentSmartReply: false,
    EnableChatTranslations: false, EnableConversationShadowing: false, EnableConversationQuality: false,
    EnableAgentLeaderboard: false, EnableAgentBotObservation: false, EnableAgentToAgent: false,
    EnableAgentSignatures: false, EnableAgentDashboard: false, EnableCustomStatuses: false,
    EnableAutoIdle: false, EnableCoBrowse: false,
    // Ticketing
    EnableTicketing: false, EnableTicketSla: false, EnableTicketAutomation: false,
    EnableTicketCsat: false, EnableTicketCoaching: false, EnableTicketMerge: false, EnableTicketReports: false,
    // Reporting
    EnableConversationReports: false, EnableMissedChatReports: false, EnableSentimentReports: false,
    EnableAgentPerformanceReports: false, EnableResponseTimeReports: false, EnableCallReports: false,
    EnableAccountUtilizationReports: false, EnableAgentAvailabilityReports: false,
    EnableAgentUtilizationReports: false, EnableAuditReporting: false, EnableIvrReports: false,
    EnableCustomThresholds: false, EnableAgentDashboard2: false,
    EnableScheduleAdherenceReports: false, EnableCustomDashboards: false,
    EnableReportEmailSubscriptions: false, EnableServiceLevelReports: false,
    EnableSurveyReports: false, EnableHealthScoreReports: false,
    EnableChatIntelligenceReport: false, EnableReportNarratives: false, EnableClickToCallReports: false,
    // Click-to-Call (all add-on)
    EnableClickToCall: false, EnableClickToCallWidget: false, EnableByokTwilioCtc: false,
    EnableCtcAiPrescreen: false, EnableCtcSmartScheduling: false,
    EnableCtcCallRecording: false, EnableCtcGeographicRouting: false,
    // Email & Outreach
    EnableEmailCampaigns: false, EnableGmailIntegration: false, EnableOutlookIntegration: false,
    EnableEmailAiResolution: false, EnableAiComposedEmails: false, EnableCopilotWrite: false,
    // CRM
    EnableHubSpot: false, EnableHubSpotSkills: false, EnableDynamics: false,
    EnableNetsuite: false, EnableNetSuiteSkills: false, EnableSalesforce: false,
    EnablePipedriveSkills: false, EnableZohoSkills: false, EnableServiceNow: false,
    // Native CRM
    EnableNativeCrm: false, EnableBuyingSignals: false, EnableCustomerHealthScore: false,
    EnableContactTimeline: false, EnableContactManager: false, EnableConversationLifecycle: false,
    EnableEcommerceSync: false, EnableVisitorTracking: false,
    EnableIntelligenceSuite: false, EnableThemeClassification: false,
    // Commerce / POS
    EnableBigCommerce: false, EnableBigCommerceBotIndexing: false,
    EnableShopify: false, EnableShopifyBotIndexing: false,
    EnableWooCommerce: false,
    EnableMagento: false, EnableSquare: false, EnableSquareRetail: false,
    EnableToastSkills: false, EnableQuickBooksSkills: false, EnableInChatPayments: false,
    EnableCloverSkills: false, EnableJobberSkills: false,
    // Hospitality
    EnableGuestySkills: false, EnableUplistingSkills: false, EnableCloudbedsSkills: false, EnableMindbodySkills: false,
    // Restaurant Booking
    EnableOpenTableSkills: false, EnableResySkills: false,
    // Productivity
    EnableAccela: false, EnableKlaviyo: false, EnableMondaySkills: false,
    EnableFormstack: false, EnableZapier: false, EnablePowerAutomate: false,
    EnableMake: false, EnableOneDriveAttachments: false, EnableTeamsNotifications: false,
    EnableSlackNotifications: false, EnableCalendarBooking: false,
    // Additional Integrations
    EnableSapSkills: false, EnableMicrosoftBookingsSkills: false,
    EnableNextiva: false, EnableVastSkills: false,
    // Recruiting Bot
    EnableRecruitingBot: false, EnableRecruitingBotEeoc: false,
    // Platform
    EnableMultipleDeployments: false, EnablePageWidgets: false,
    // Outbound Campaigns
    EnableOutboundCampaigns: false, EnableOutboundIvr: false, EnableSmartCallback: false,
    EnableAdvancedCampaignReports: false, EnableLandingPages: false,
    EnableProactiveEngagement: false,
    // Workforce & Operations
    EnableWorkforceManagement: false, EnableSelfServeSeats: false, EnableSoftphone: false,
    EnableOperationalAlerts: false, EnableOpsIntelligence: false,
    EnableUsageAlerts: true, EnableSmsAlerts: false, EnablePortalCustomDomain: false,
    // Security & Compliance
    EnableEnterpriseSso: false, EnableAdvancedMfa: false, EnablePasskeys: false,
    EnableIpBlocking: false, EnableAgentIpRestriction: false, EnableSessionTimeout: false,
    EnableConcurrentSessionLimit: false, EnableDataRetention: false,
    EnableScheduledPurge: false, EnableTranscriptStorage: false, EnableMessageScrubbing: false,
    EnablePiiRedaction: false, EnableComplianceMode: false,
  },
  pro: {
    // Channels
    EnableWeb: true, EnableSms: true, EnableEmail: true, EnableWhatsapp: true,
    EnableFacebook: true, EnableInstagram: false, EnableTikTok: false, EnableAppleMessages: false,
    EnableRcs: false, EnableRcsRichMessages: false,
    // AI & Automation
    EnableAI: true, EnableWorkflowRules: true, EnableAutomation: true,
    EnableMcp: true, EnableQuickStartWizards: true, EnableBotVoice: true,
    EnableOrchestration: false, EnableAvatarMode: false,
    EnableBotEmailSkill: true, EnableBotSmsSkill: false,
    EnableAdvancedAiSkills: false, EnableDestructiveAiSkills: false,
    EnableAiOverage: true, EnableByokAi: false,
    // Knowledge Base
    EnableKnowledgeBase: true, EnableKbBotIndexing: true,
    EnableSemanticKbSearch: true, EnableConversationTraining: false,
    EnableScraperBotIndexing: true, EnableYoutubeBotIndexing: false,
    EnableFileIngestion: true, EnableOneDriveIngestionSync: false,
    EnableJsonImport: true, EnableScraperAutoRebuild: false,
    // Routing
    EnableAutoRouting: true, EnableTierEscalation: false, EnablePriorityQueue: false, EnableVideoChat: false,
    // Agent Features
    EnableCollisionDetection: true, EnableAgentAssist: true, EnableAgentSmartReply: false,
    EnableChatTranslations: false, EnableConversationShadowing: true, EnableConversationQuality: false,
    EnableAgentLeaderboard: true, EnableAgentBotObservation: false, EnableAgentToAgent: true,
    EnableAgentSignatures: true, EnableAgentDashboard: true, EnableCustomStatuses: false,
    EnableAutoIdle: false, EnableCoBrowse: false,
    // Ticketing
    EnableTicketing: true, EnableTicketSla: false, EnableTicketAutomation: true,
    EnableTicketCsat: true, EnableTicketCoaching: false, EnableTicketMerge: true, EnableTicketReports: true,
    // Reporting
    EnableConversationReports: true, EnableMissedChatReports: true, EnableSentimentReports: false,
    EnableAgentPerformanceReports: true, EnableResponseTimeReports: true, EnableCallReports: false,
    EnableAccountUtilizationReports: false, EnableAgentAvailabilityReports: true,
    EnableAgentUtilizationReports: false, EnableAuditReporting: false, EnableIvrReports: false,
    EnableCustomThresholds: false, EnableAgentDashboard2: false,
    EnableScheduleAdherenceReports: false, EnableCustomDashboards: false,
    EnableReportEmailSubscriptions: false, EnableServiceLevelReports: false,
    EnableSurveyReports: false, EnableHealthScoreReports: false,
    EnableChatIntelligenceReport: false, EnableReportNarratives: false, EnableClickToCallReports: false,
    // Click-to-Call (all add-on)
    EnableClickToCall: false, EnableClickToCallWidget: false, EnableByokTwilioCtc: false,
    EnableCtcAiPrescreen: false, EnableCtcSmartScheduling: false,
    EnableCtcCallRecording: false, EnableCtcGeographicRouting: false,
    // Email & Outreach
    EnableEmailCampaigns: false, EnableGmailIntegration: true, EnableOutlookIntegration: true,
    EnableEmailAiResolution: false, EnableAiComposedEmails: false, EnableCopilotWrite: false,
    // CRM
    EnableHubSpot: false, EnableHubSpotSkills: false, EnableDynamics: false,
    EnableNetsuite: false, EnableNetSuiteSkills: false, EnableSalesforce: false,
    EnablePipedriveSkills: false, EnableZohoSkills: false, EnableServiceNow: false,
    // Native CRM
    EnableNativeCrm: false, EnableBuyingSignals: false, EnableCustomerHealthScore: false,
    EnableContactTimeline: true, EnableContactManager: true, EnableConversationLifecycle: false,
    EnableEcommerceSync: false, EnableVisitorTracking: false,
    EnableIntelligenceSuite: false, EnableThemeClassification: false,
    // Commerce / POS
    EnableBigCommerce: true, EnableBigCommerceBotIndexing: true,
    EnableShopify: true, EnableShopifyBotIndexing: true,
    EnableWooCommerce: false,
    EnableMagento: false, EnableSquare: false, EnableSquareRetail: false,
    EnableToastSkills: false, EnableQuickBooksSkills: false, EnableInChatPayments: false,
    EnableCloverSkills: false, EnableJobberSkills: false,
    // Hospitality
    EnableGuestySkills: false, EnableUplistingSkills: false, EnableCloudbedsSkills: false, EnableMindbodySkills: false,
    // Restaurant Booking
    EnableOpenTableSkills: false, EnableResySkills: false,
    // Productivity
    EnableAccela: false, EnableKlaviyo: false, EnableMondaySkills: false,
    EnableFormstack: false, EnableZapier: false, EnablePowerAutomate: false,
    EnableMake: false, EnableOneDriveAttachments: true, EnableTeamsNotifications: true,
    EnableSlackNotifications: true, EnableCalendarBooking: true,
    // Additional Integrations
    EnableSapSkills: false, EnableMicrosoftBookingsSkills: false,
    EnableNextiva: false, EnableVastSkills: false,
    // Recruiting Bot
    EnableRecruitingBot: false, EnableRecruitingBotEeoc: false,
    // Platform
    EnableMultipleDeployments: true, EnablePageWidgets: true,
    // Outbound Campaigns
    EnableOutboundCampaigns: false, EnableOutboundIvr: false, EnableSmartCallback: false,
    EnableAdvancedCampaignReports: false, EnableLandingPages: false,
    EnableProactiveEngagement: false,
    // Workforce & Operations
    EnableWorkforceManagement: false, EnableSelfServeSeats: false, EnableSoftphone: false,
    EnableOperationalAlerts: true, EnableOpsIntelligence: false,
    EnableUsageAlerts: true, EnableSmsAlerts: false, EnablePortalCustomDomain: false,
    // Security & Compliance
    EnableEnterpriseSso: false, EnableAdvancedMfa: false, EnablePasskeys: false,
    EnableIpBlocking: false, EnableAgentIpRestriction: false, EnableSessionTimeout: false,
    EnableConcurrentSessionLimit: false, EnableDataRetention: false,
    EnableScheduledPurge: false, EnableTranscriptStorage: false, EnableMessageScrubbing: false,
    EnablePiiRedaction: false, EnableComplianceMode: false,
  },
  enterprise: {
    // Channels
    EnableWeb: true, EnableSms: true, EnableEmail: true, EnableWhatsapp: true,
    EnableFacebook: true, EnableInstagram: true, EnableTikTok: true, EnableAppleMessages: true,
    EnableRcs: false, EnableRcsRichMessages: false,
    // AI & Automation
    EnableAI: true, EnableWorkflowRules: true, EnableAutomation: true,
    EnableMcp: true, EnableQuickStartWizards: true, EnableBotVoice: true,
    EnableOrchestration: true, EnableAvatarMode: true,
    EnableBotEmailSkill: true, EnableBotSmsSkill: true,
    EnableAdvancedAiSkills: false, EnableDestructiveAiSkills: false,
    EnableAiOverage: true, EnableByokAi: true,
    // Knowledge Base
    EnableKnowledgeBase: true, EnableKbBotIndexing: true,
    EnableSemanticKbSearch: true, EnableConversationTraining: true,
    EnableScraperBotIndexing: true, EnableYoutubeBotIndexing: true,
    EnableFileIngestion: true, EnableOneDriveIngestionSync: true,
    EnableJsonImport: true, EnableScraperAutoRebuild: true,
    // Routing
    EnableAutoRouting: true, EnableTierEscalation: true, EnablePriorityQueue: true, EnableVideoChat: true,
    // Agent Features
    EnableCollisionDetection: true, EnableAgentAssist: true, EnableAgentSmartReply: true,
    EnableChatTranslations: true, EnableConversationShadowing: true, EnableConversationQuality: true,
    EnableAgentLeaderboard: true, EnableAgentBotObservation: true, EnableAgentToAgent: true,
    EnableAgentSignatures: true, EnableAgentDashboard: true, EnableCustomStatuses: true,
    EnableAutoIdle: true, EnableCoBrowse: true,
    // Ticketing
    EnableTicketing: true, EnableTicketSla: true, EnableTicketAutomation: true,
    EnableTicketCsat: true, EnableTicketCoaching: true, EnableTicketMerge: true, EnableTicketReports: true,
    // Reporting
    EnableConversationReports: true, EnableMissedChatReports: true, EnableSentimentReports: true,
    EnableAgentPerformanceReports: true, EnableResponseTimeReports: true, EnableCallReports: true,
    EnableAccountUtilizationReports: true, EnableAgentAvailabilityReports: true,
    EnableAgentUtilizationReports: true, EnableAuditReporting: true, EnableIvrReports: true,
    EnableCustomThresholds: true, EnableAgentDashboard2: false,
    EnableScheduleAdherenceReports: true, EnableCustomDashboards: true,
    EnableReportEmailSubscriptions: true, EnableServiceLevelReports: true,
    EnableSurveyReports: true, EnableHealthScoreReports: true,
    EnableChatIntelligenceReport: true, EnableReportNarratives: true, EnableClickToCallReports: false,
    // Click-to-Call (all add-on)
    EnableClickToCall: false, EnableClickToCallWidget: false, EnableByokTwilioCtc: false,
    EnableCtcAiPrescreen: false, EnableCtcSmartScheduling: false,
    EnableCtcCallRecording: false, EnableCtcGeographicRouting: false,
    // Email & Outreach
    EnableEmailCampaigns: false, EnableGmailIntegration: true, EnableOutlookIntegration: true,
    EnableEmailAiResolution: true, EnableAiComposedEmails: true, EnableCopilotWrite: true,
    // CRM
    EnableHubSpot: true, EnableHubSpotSkills: true, EnableDynamics: true,
    EnableNetsuite: true, EnableNetSuiteSkills: true, EnableSalesforce: true,
    EnablePipedriveSkills: true, EnableZohoSkills: true, EnableServiceNow: true,
    // Native CRM
    EnableNativeCrm: true, EnableBuyingSignals: true, EnableCustomerHealthScore: true,
    EnableContactTimeline: true, EnableContactManager: true, EnableConversationLifecycle: true,
    EnableEcommerceSync: true, EnableVisitorTracking: true,
    EnableIntelligenceSuite: true, EnableThemeClassification: true,
    // Commerce / POS
    EnableBigCommerce: true, EnableBigCommerceBotIndexing: true,
    EnableShopify: true, EnableShopifyBotIndexing: true,
    EnableWooCommerce: true,
    EnableMagento: true, EnableSquare: true, EnableSquareRetail: true,
    EnableToastSkills: true, EnableQuickBooksSkills: true, EnableInChatPayments: false,
    EnableCloverSkills: true, EnableJobberSkills: true,
    // Hospitality
    EnableGuestySkills: true, EnableUplistingSkills: true, EnableCloudbedsSkills: true, EnableMindbodySkills: true,
    // Restaurant Booking
    EnableOpenTableSkills: true, EnableResySkills: true,
    // Productivity
    EnableAccela: true, EnableKlaviyo: true, EnableMondaySkills: true,
    EnableFormstack: true, EnableZapier: true, EnablePowerAutomate: true,
    EnableMake: true, EnableOneDriveAttachments: true, EnableTeamsNotifications: true,
    EnableSlackNotifications: true, EnableCalendarBooking: true,
    // Additional Integrations
    EnableSapSkills: true, EnableMicrosoftBookingsSkills: true,
    EnableNextiva: false, EnableVastSkills: false,
    // Recruiting Bot
    EnableRecruitingBot: false, EnableRecruitingBotEeoc: false,
    // Platform
    EnableMultipleDeployments: true, EnablePageWidgets: true,
    // Outbound Campaigns
    EnableOutboundCampaigns: false, EnableOutboundIvr: false, EnableSmartCallback: false,
    EnableAdvancedCampaignReports: false, EnableLandingPages: false,
    EnableProactiveEngagement: true,
    // Workforce & Operations
    EnableWorkforceManagement: true, EnableSelfServeSeats: true, EnableSoftphone: false,
    EnableOperationalAlerts: true, EnableOpsIntelligence: true,
    EnableUsageAlerts: true, EnableSmsAlerts: true, EnablePortalCustomDomain: true,
    // Security & Compliance
    EnableEnterpriseSso: true, EnableAdvancedMfa: true, EnablePasskeys: true,
    EnableIpBlocking: true, EnableAgentIpRestriction: true, EnableSessionTimeout: true,
    EnableConcurrentSessionLimit: true, EnableDataRetention: true,
    EnableScheduledPurge: true, EnableTranscriptStorage: true, EnableMessageScrubbing: true,
    EnablePiiRedaction: true, EnableComplianceMode: true,
  }
};

export interface FeatureItem {
  key: string;
  label: string;
}

export interface FeatureGroup {
  label: string;
  features: FeatureItem[];
}

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    label: "Inbound Channels",
    features: [
      { key: "EnableWeb",           label: "Web Messenger" },
      { key: "EnableSms",           label: "SMS" },
      { key: "EnableEmail",         label: "Email" },
      { key: "EnableWhatsapp",      label: "WhatsApp" },
      { key: "EnableFacebook",      label: "Facebook Messenger" },
      { key: "EnableInstagram",     label: "Instagram DMs" },
      { key: "EnableTikTok",        label: "TikTok DMs" },
      { key: "EnableAppleMessages", label: "Apple Messages for Business" },
      { key: "EnableRcs",           label: "RCS (Rich Communication Services)" },
      { key: "EnableRcsRichMessages", label: "RCS Rich Messages (cards, carousels)" },
    ]
  },
  {
    label: "AI & Automation",
    features: [
      { key: "EnableAI",                  label: "AI Chatbots" },
      { key: "EnableWorkflowRules",       label: "Workflow Rules" },
      { key: "EnableAutomation",          label: "Automation" },
      { key: "EnableMcp",                 label: "MCP Servers" },
      { key: "EnableQuickStartWizards",   label: "Quick Setup Wizards" },
      { key: "EnableBotVoice",            label: "ElevenLabs Bot Voice (TTS)" },
      { key: "EnableAvatarMode",          label: "Talking Avatar (Simli AI — animated lip-sync video avatar)" },
      { key: "EnableOrchestration",       label: "AI Orchestration (multi-bot delegation)" },
      { key: "EnableBotEmailSkill",       label: "Bot Email Skill" },
      { key: "EnableBotSmsSkill",         label: "Bot SMS Skill (outbound bot texts)" },
      { key: "EnableAdvancedAiSkills",    label: "Advanced AI Skills (Write tier — create/update records)" },
      { key: "EnableDestructiveAiSkills", label: "Destructive AI Skills (cancel orders / issue refunds)" },
      { key: "EnableConversationTraining",label: "Conversation Training (bot learns from resolved chats)" },
      { key: "EnableAiOverage",           label: "AI Conversation Overage Billing" },
      { key: "EnableByokAi",              label: "BYOK AI (Bring Your Own Azure OpenAI / Anthropic Key)" },
      { key: "EnableCopilotWrite",        label: "Copilot Write (AI drafts agent replies)" },
    ]
  },
  {
    label: "Knowledge Base & Ingestion",
    features: [
      { key: "EnableKnowledgeBase",       label: "Knowledge Base (public help site)" },
      { key: "EnableKbBotIndexing",       label: "KB → Bot Index (articles feed the AI bot)" },
      { key: "EnableSemanticKbSearch",    label: "Semantic KB Search (AI search bar + REST API)" },
      { key: "EnableConversationTraining",label: "Conversation Training" },
      { key: "EnableScraperBotIndexing",  label: "Scraper Bot Indexing (crawl website → KB)" },
      { key: "EnableYoutubeBotIndexing",  label: "YouTube Bot Indexing (transcribe videos → KB)" },
      { key: "EnableFileIngestion",       label: "File Ingestion (PDF / DOCX / XLSX → KB)" },
      { key: "EnableOneDriveIngestionSync", label: "OneDrive/SharePoint Auto-Sync → KB" },
      { key: "EnableJsonImport",          label: "JSON KB Import (bulk article upload via API)" },
      { key: "EnableScraperAutoRebuild",  label: "Scraper Auto-Rebuild (scheduled re-crawl)" },
    ]
  },
  {
    label: "Routing",
    features: [
      { key: "EnableAutoRouting",    label: "Auto-Routing (Round Robin / Least Busy)" },
      { key: "EnableTierEscalation", label: "Tier Escalation Routing (Tier 1/2/3 ring timeouts)" },
      { key: "EnablePriorityQueue",  label: "Priority / VIP Queue" },
      { key: "EnableVideoChat",      label: "Video Chat (WebRTC P2P)" },
    ]
  },
  {
    label: "Agent Features",
    features: [
      { key: "EnableCollisionDetection",  label: "Collision Detection (see when another agent is typing)" },
      { key: "EnableAgentAssist",         label: "Agent Assist (real-time AI coaching in live chat)" },
      { key: "EnableAgentSmartReply",     label: "Agent Smart Reply (AI-suggested reply buttons)" },
      { key: "EnableChatTranslations",    label: "Chat Translations (real-time language translation)" },
      { key: "EnableConversationShadowing", label: "Conversation Shadowing (supervisor watches live)" },
      { key: "EnableConversationQuality", label: "Conversation Quality Scoring (QA automation)" },
      { key: "EnableAgentLeaderboard",    label: "Agent Leaderboard (gamification)" },
      { key: "EnableAgentBotObservation", label: "Agent Bot Observation (watch bot conversations live)" },
      { key: "EnableAgentToAgent",        label: "Agent-to-Agent Chat (internal messaging)" },
      { key: "EnableAgentSignatures",     label: "Agent Signatures (email / chat sign-off)" },
      { key: "EnableAgentDashboard",      label: "Agent Dashboard (live queue metrics)" },
      { key: "EnableCustomStatuses",      label: "Custom Agent Statuses" },
      { key: "EnableAutoIdle",            label: "Auto-Idle Detection (mark away after inactivity)" },
      { key: "EnableCoBrowse",            label: "Co-Browse (agent shares visitor browser session)" },
    ]
  },
  {
    label: "Ticketing",
    features: [
      { key: "EnableTicketing",        label: "Ticketing (email/chat → tickets)" },
      { key: "EnableTicketSla",        label: "Ticket SLA (response & resolution timers)" },
      { key: "EnableTicketAutomation", label: "Ticket Automation (rules-based routing/tagging)" },
      { key: "EnableTicketCsat",       label: "Ticket CSAT Surveys" },
      { key: "EnableTicketCoaching",   label: "Ticket Coaching (AI quality review)" },
      { key: "EnableTicketMerge",      label: "Ticket Merge" },
      { key: "EnableTicketReports",    label: "Ticket Reports" },
    ]
  },
  {
    label: "Reporting & Analytics",
    features: [
      { key: "EnableConversationReports",     label: "Conversation Reports" },
      { key: "EnableMissedChatReports",       label: "Missed Chat Reports" },
      { key: "EnableSentimentReports",        label: "Sentiment Reports" },
      { key: "EnableAgentPerformanceReports", label: "Agent Performance Reports" },
      { key: "EnableResponseTimeReports",     label: "Response Time Reports" },
      { key: "EnableCallReports",             label: "Call Reports" },
      { key: "EnableAccountUtilizationReports", label: "Account Utilization Reports" },
      { key: "EnableAgentAvailabilityReports",  label: "Agent Availability Reports" },
      { key: "EnableAgentUtilizationReports",   label: "Agent Utilization Reports" },
      { key: "EnableAuditReporting",          label: "Audit Reporting" },
      { key: "EnableIvrReports",              label: "IVR Reports" },
      { key: "EnableCustomThresholds",        label: "Custom Report Thresholds" },
      { key: "EnableScheduleAdherenceReports", label: "Schedule Adherence Reports" },
      { key: "EnableCustomDashboards",        label: "Custom Dashboards" },
      { key: "EnableReportEmailSubscriptions", label: "Report Email Subscriptions (scheduled delivery)" },
      { key: "EnableServiceLevelReports",     label: "Service Level Reports" },
      { key: "EnableSurveyReports",           label: "Survey Reports" },
      { key: "EnableHealthScoreReports",      label: "Customer Health Score Reports" },
      { key: "EnableChatIntelligenceReport",  label: "Chat Intelligence Report (themes + sentiment trends)" },
      { key: "EnableReportNarratives",        label: "Report Narratives (AI-written report summaries)" },
      { key: "EnableClickToCallReports",      label: "Click-to-Call Reports" },
    ]
  },
  {
    label: "Voice & IVR",
    features: [
      { key: "EnableOutboundIvr",   label: "Outbound IVR Campaigns" },
      { key: "EnableSmartCallback", label: "Smart Callback (missed-call → auto outbound menu)" },
      { key: "EnableBotVoice",      label: "ElevenLabs Bot Voice TTS" },
      { key: "EnableIvrReports",    label: "IVR Reports" },
    ]
  },
  {
    label: "Click-to-Call",
    features: [
      { key: "EnableClickToCall",          label: "Click-to-Call" },
      { key: "EnableClickToCallWidget",    label: "Click-to-Call Widget" },
      { key: "EnableByokTwilioCtc",        label: "BYOK Twilio (bring your own Twilio account)" },
      { key: "EnableCtcAiPrescreen",       label: "CTC AI Prescreen (bot qualifies caller before connect)" },
      { key: "EnableCtcSmartScheduling",   label: "CTC Smart Scheduling (offer callback time slots)" },
      { key: "EnableCtcCallRecording",     label: "CTC Call Recording" },
      { key: "EnableCtcGeographicRouting", label: "CTC Geographic Routing (route by caller location)" },
      { key: "EnableClickToCallReports",   label: "Click-to-Call Reports" },
    ]
  },
  {
    label: "Email & Outreach",
    features: [
      { key: "EnableEmailCampaigns",    label: "Email Campaigns (outbound marketing)" },
      { key: "EnableGmailIntegration",  label: "Gmail Inbox (inbound email ticketing)" },
      { key: "EnableOutlookIntegration", label: "Outlook Inbox (inbound email ticketing)" },
      { key: "EnableEmailAiResolution", label: "Email AI Resolution (auto-draft/close email tickets)" },
      { key: "EnableAiComposedEmails",  label: "AI Composed Emails (bot drafts outbound emails)" },
      { key: "EnableCopilotWrite",      label: "Copilot Write (AI drafts agent replies)" },
    ]
  },
  {
    label: "CRM Integrations",
    features: [
      { key: "EnableHubSpot",         label: "HubSpot CRM" },
      { key: "EnableHubSpotSkills",   label: "HubSpot AI Skills" },
      { key: "EnableDynamics",        label: "Microsoft Dynamics 365" },
      { key: "EnableNetsuite",        label: "NetSuite ERP" },
      { key: "EnableNetSuiteSkills",  label: "NetSuite AI Skills" },
      { key: "EnableSalesforce",      label: "Salesforce" },
      { key: "EnablePipedriveSkills", label: "Pipedrive AI Skills" },
      { key: "EnableZohoSkills",      label: "Zoho CRM AI Skills" },
      { key: "EnableServiceNow",      label: "ServiceNow ITSM" },
    ]
  },
  {
    label: "Native CRM & Intelligence",
    features: [
      { key: "EnableNativeCrm",           label: "Native Velaro CRM (deals, contacts, companies)" },
      { key: "EnableBuyingSignals",        label: "Buying Signals (AI intent detection)" },
      { key: "EnableCustomerHealthScore",  label: "Customer Health Score" },
      { key: "EnableContactTimeline",      label: "Contact Timeline" },
      { key: "EnableContactManager",       label: "Contact Manager" },
      { key: "EnableConversationLifecycle",label: "Conversation Lifecycle Tracking" },
      { key: "EnableEcommerceSync",        label: "E-Commerce Sync (shopper profiles + purchase-based segmentation)" },
      { key: "EnableVisitorTracking",      label: "Visitor Tracking (page views, session data)" },
      { key: "EnableIntelligenceSuite",    label: "Intelligence Suite (themes, trends, sentiment)" },
      { key: "EnableThemeClassification",  label: "Theme Classification (AI auto-tag conversation topics)" },
    ]
  },
  {
    label: "Commerce / POS",
    features: [
      { key: "EnableBigCommerce",          label: "BigCommerce" },
      { key: "EnableBigCommerceBotIndexing", label: "BigCommerce Product Catalog → Bot Index" },
      { key: "EnableShopify",              label: "Shopify" },
      { key: "EnableShopifyBotIndexing",   label: "Shopify Product Catalog → Bot Index" },
      { key: "EnableWooCommerce",          label: "WooCommerce" },
      { key: "EnableMagento",              label: "Magento / Adobe Commerce" },
      { key: "EnableSquare",               label: "Square (Appointments / T1)" },
      { key: "EnableSquareRetail",         label: "Square Retail (Catalog / T2)" },
      { key: "EnableToastSkills",          label: "Toast POS AI Skills" },
      { key: "EnableQuickBooksSkills",     label: "QuickBooks AI Skills" },
      { key: "EnableCloverSkills",         label: "Clover POS AI Skills" },
      { key: "EnableJobberSkills",         label: "Jobber Field Service AI Skills" },
      { key: "EnableInChatPayments",       label: "In-Chat Payments (Stripe)" },
    ]
  },
  {
    label: "Hospitality",
    features: [
      { key: "EnableGuestySkills",     label: "Guesty (Vacation Rental)" },
      { key: "EnableUplistingSkills",  label: "Uplisting (Short-Term Rental)" },
      { key: "EnableCloudbedsSkills",  label: "Cloudbeds (Hotel PMS)" },
      { key: "EnableMindbodySkills",   label: "Mindbody (Fitness / Wellness)" },
    ]
  },
  {
    label: "Restaurant Booking",
    features: [
      { key: "EnableOpenTableSkills", label: "OpenTable (Restaurant Reservations)" },
      { key: "EnableResySkills",      label: "Resy (Restaurant Reservations)" },
    ]
  },
  {
    label: "Additional Integrations",
    features: [
      { key: "EnableSapSkills",               label: "SAP ERP AI Skills (B1 + S/4HANA)" },
      { key: "EnableMicrosoftBookingsSkills",  label: "Microsoft Bookings AI Skills" },
      { key: "EnableNextiva",                  label: "Nextiva VoIP CTI (screen pop, click-to-call)" },
      { key: "EnableVastSkills",               label: "VAST Retail / Kerridge CS (tire shop DMS)" },
      { key: "EnableAccela",                   label: "Accela Civic Platform (Government)" },
    ]
  },
  {
    label: "Productivity & Automation",
    features: [
      { key: "EnableKlaviyo",             label: "Klaviyo" },
      { key: "EnableMondaySkills",        label: "Monday.com AI Skills" },
      { key: "EnableFormstack",           label: "Formstack" },
      { key: "EnableZapier",              label: "Zapier" },
      { key: "EnablePowerAutomate",       label: "Power Automate" },
      { key: "EnableMake",                label: "Make.com" },
      { key: "EnableOneDriveAttachments", label: "OneDrive File Attachments in Chat" },
      { key: "EnableTeamsNotifications",  label: "Teams Notifications" },
      { key: "EnableSlackNotifications",  label: "Slack Notifications" },
      { key: "EnableCalendarBooking",     label: "Calendar Booking (Outlook / Calendly)" },
    ]
  },
  {
    label: "Recruiting Bot",
    features: [
      { key: "EnableRecruitingBot",     label: "Recruiting Bot (job listings, application intake)" },
      { key: "EnableRecruitingBotEeoc", label: "Recruiting Bot EEOC Compliance Questions" },
    ]
  },
  {
    label: "Outbound Campaigns",
    features: [
      { key: "EnableOutboundCampaigns",       label: "Email & SMS Campaigns" },
      { key: "EnableOutboundIvr",             label: "Outbound IVR Campaigns" },
      { key: "EnableSmartCallback",           label: "Smart Callback IVR" },
      { key: "EnableAdvancedCampaignReports", label: "Advanced Campaign Reports" },
      { key: "EnableLandingPages",            label: "Campaign Landing Pages" },
      { key: "EnableProactiveEngagement",     label: "Proactive Engagement Engine (page-aware invitations)" },
    ]
  },
  {
    label: "Workforce & Operations",
    features: [
      { key: "EnableWorkforceManagement",  label: "Workforce Management (scheduling, adherence)" },
      { key: "EnableSelfServeSeats",       label: "Self-Serve Seat Management" },
      { key: "EnableSoftphone",            label: "Softphone (WebRTC browser-based calling)" },
      { key: "EnableOperationalAlerts",    label: "Operational Alerts (queue depth / wait time SMS alerts)" },
      { key: "EnableOpsIntelligence",      label: "Ops Intelligence (AI operational recommendations)" },
      { key: "EnableUsageAlerts",          label: "Usage Alerts (approaching limits warning)" },
      { key: "EnableSmsAlerts",            label: "SMS Alerts (ops alerts sent via text)" },
      { key: "EnablePortalCustomDomain",   label: "Portal Custom Domain (white-label KB URL)" },
    ]
  },
  {
    label: "Platform",
    features: [
      { key: "EnableMultipleDeployments", label: "Multiple Deployments (multiple chat widgets)" },
      { key: "EnablePageWidgets",         label: "Page Widgets (contextual slide-out panels)" },
    ]
  },
  {
    label: "Security & Compliance",
    features: [
      { key: "EnableEnterpriseSso",          label: "Enterprise SSO (SAML / OIDC)" },
      { key: "EnableAdvancedMfa",            label: "Advanced MFA (TOTP / push)" },
      { key: "EnablePasskeys",               label: "Passkeys / FIDO2" },
      { key: "EnableIpBlocking",             label: "Visitor IP Blocking" },
      { key: "EnableAgentIpRestriction",     label: "Agent Login IP Restriction" },
      { key: "EnableSessionTimeout",         label: "Session Timeout" },
      { key: "EnableConcurrentSessionLimit", label: "Concurrent Session Limit" },
      { key: "EnableDataRetention",          label: "Data Retention (GDPR Purge)" },
      { key: "EnableScheduledPurge",         label: "Scheduled Auto-Purge" },
      { key: "EnableTranscriptStorage",      label: "BYOS Transcript Storage (Azure Blob / S3)" },
      { key: "EnableMessageScrubbing",       label: "Message Scrubbing (PII pattern removal)" },
      { key: "EnablePiiRedaction",           label: "PII Redaction (AI-powered — names, emails, SSNs)" },
      { key: "EnableComplianceMode",         label: "Compliance Mode (post-chat review before archiving)" },
    ]
  },
];

// ── Product Packages ──────────────────────────────────────────────────────────
// A package is a named bundle of feature flags. Enabling a package in the
// DatabaseTool sets ALL its flags to true in one click. Disabling sets them all
// to false. Individual flags can still be overridden after applying a package.
// Pro customers pick 1-2 bundles; enterprise gets all. MaxIntegrationBundles on
// the subscription controls how many a Pro site may activate.
export interface ProductPackage {
  id: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  /** Boolean feature keys this bundle enables (all set to true on apply). */
  features: string[];
  /** Numeric limits set on apply: { SubscriptionColumnName: value }. */
  limits?: Record<string, number>;
  /** Which plan tier this package is included in by default. */
  includedInPlans: Array<"basic" | "pro" | "enterprise">;
}

export const PRODUCT_PACKAGES: ProductPackage[] = [
  // ── Channels ─────────────────────────────────────────────────────────────
  {
    id: "channels-pro",
    label: "Channels — Pro",
    description: "SMS, email, WhatsApp, and Facebook Messenger in addition to web chat.",
    icon: "fa-comments",
    color: "blue",
    features: ["EnableSms", "EnableEmail", "EnableWhatsapp", "EnableFacebook"],
    includedInPlans: ["pro", "enterprise"],
  },
  {
    id: "channels-enterprise",
    label: "Channels — Enterprise",
    description: "Instagram DMs, TikTok DMs, Apple Messages for Business, and RCS.",
    icon: "fa-hashtag",
    color: "pink",
    features: ["EnableInstagram", "EnableTikTok", "EnableAppleMessages"],
    includedInPlans: ["enterprise"],
  },
  {
    id: "rcs",
    label: "RCS Messaging",
    description: "Rich Communication Services — branded messages, carousels, quick-reply buttons, and read receipts over SMS infrastructure.",
    icon: "fa-message",
    color: "green",
    features: ["EnableRcs", "EnableRcsRichMessages"],
    includedInPlans: [],
  },
  // ── AI ───────────────────────────────────────────────────────────────────
  {
    id: "ai-enterprise",
    label: "AI Enterprise",
    description: "MCP servers, AI orchestration (multi-bot delegation), BYOK AI, advanced write-tier skills, destructive skills, and conversation training.",
    icon: "fa-brain",
    color: "violet",
    features: ["EnableMcp", "EnableOrchestration", "EnableByokAi", "EnableAdvancedAiSkills", "EnableDestructiveAiSkills", "EnableConversationTraining", "EnableBotVoice", "EnableBotEmailSkill"],
    includedInPlans: ["enterprise"],
  },
  {
    id: "talking-avatar",
    label: "Talking Avatar",
    description: "Simli AI animated lip-sync video avatar — bot replies delivered as a speaking on-screen character. Requires ElevenLabs bot voice.",
    icon: "fa-circle-user",
    color: "purple",
    features: ["EnableAvatarMode", "EnableBotVoice"],
    includedInPlans: [],
  },
  // ── Knowledge Base ────────────────────────────────────────────────────────
  {
    id: "knowledge-base",
    label: "Knowledge Base",
    description: "Public help site, AI-powered semantic search, bot article indexing, and the KB REST API.",
    icon: "fa-book-open",
    color: "blue",
    features: ["EnableKnowledgeBase", "EnableKbBotIndexing"],
    includedInPlans: ["basic", "pro", "enterprise"],
  },
  {
    id: "ai-answers",
    label: "AI Answers",
    description: "KB bot auto-answer + semantic search on the public help site. Deflects tickets before they reach an agent.",
    icon: "fa-robot",
    color: "violet",
    features: ["EnableAI", "EnableKnowledgeBase", "EnableKbBotIndexing", "EnableSemanticKbSearch"],
    limits: { MaxAiConversationsPerMonth: 2000 },
    includedInPlans: ["pro", "enterprise"],
  },
  {
    id: "kb-ingestion",
    label: "KB Ingestion Suite",
    description: "Scraper bot crawling, YouTube transcript indexing, file upload (PDF/DOCX), OneDrive/SharePoint sync, and scheduled auto-rebuild.",
    icon: "fa-database",
    color: "cyan",
    features: ["EnableScraperBotIndexing", "EnableYoutubeBotIndexing", "EnableFileIngestion", "EnableOneDriveIngestionSync", "EnableJsonImport", "EnableScraperAutoRebuild"],
    includedInPlans: ["enterprise"],
  },
  // ── Routing ───────────────────────────────────────────────────────────────
  {
    id: "advanced-routing",
    label: "Advanced Routing",
    description: "Tier escalation routing with per-tier ring timeouts, priority/VIP queue, and video chat.",
    icon: "fa-layer-group",
    color: "violet",
    features: ["EnableAutoRouting", "EnableTierEscalation", "EnablePriorityQueue", "EnableVideoChat"],
    includedInPlans: ["enterprise"],
  },
  // ── Agent ─────────────────────────────────────────────────────────────────
  {
    id: "agent-intelligence",
    label: "Agent Intelligence",
    description: "AI assist, smart reply, chat translations, conversation quality scoring, leaderboard, co-browse, and supervisor shadowing.",
    icon: "fa-headset",
    color: "indigo",
    features: ["EnableAgentAssist", "EnableAgentSmartReply", "EnableChatTranslations", "EnableConversationQuality", "EnableAgentLeaderboard", "EnableCoBrowse", "EnableConversationShadowing", "EnableAgentBotObservation", "EnableCollisionDetection"],
    includedInPlans: ["enterprise"],
  },
  // ── Ticketing ─────────────────────────────────────────────────────────────
  {
    id: "ticketing",
    label: "Ticketing",
    description: "Email and chat-to-ticket conversion, SLA timers, automation rules, CSAT surveys, AI coaching, merge, and ticket reports.",
    icon: "fa-ticket",
    color: "orange",
    features: ["EnableTicketing", "EnableTicketSla", "EnableTicketAutomation", "EnableTicketCsat", "EnableTicketCoaching", "EnableTicketMerge", "EnableTicketReports"],
    includedInPlans: ["pro", "enterprise"],
  },
  // ── Reporting ─────────────────────────────────────────────────────────────
  {
    id: "reporting",
    label: "Advanced Reporting",
    description: "Full reporting suite: sentiment, utilization, service level, schedule adherence, custom dashboards, report email subscriptions, and AI narratives.",
    icon: "fa-chart-bar",
    color: "emerald",
    features: ["EnableConversationReports", "EnableSentimentReports", "EnableAgentPerformanceReports", "EnableResponseTimeReports", "EnableAccountUtilizationReports", "EnableAgentUtilizationReports", "EnableAuditReporting", "EnableScheduleAdherenceReports", "EnableCustomDashboards", "EnableReportEmailSubscriptions", "EnableServiceLevelReports", "EnableSurveyReports", "EnableChatIntelligenceReport", "EnableReportNarratives"],
    includedInPlans: ["enterprise"],
  },
  // ── Voice & Click-to-Call ─────────────────────────────────────────────────
  {
    id: "voice-ivr",
    label: "Voice & IVR",
    description: "Inbound IVR, outbound IVR campaigns, smart callback, and ElevenLabs bot voice TTS.",
    icon: "fa-phone-volume",
    color: "teal",
    features: ["EnableOutboundIvr", "EnableSmartCallback", "EnableBotVoice"],
    includedInPlans: ["enterprise"],
  },
  {
    id: "click-to-call",
    label: "Click-to-Call",
    description: "Browser-based click-to-call widget with AI prescreen, smart scheduling, call recording, geographic routing, and BYOK Twilio.",
    icon: "fa-phone-flip",
    color: "green",
    features: ["EnableClickToCall", "EnableClickToCallWidget", "EnableCtcAiPrescreen", "EnableCtcSmartScheduling", "EnableCtcCallRecording", "EnableCtcGeographicRouting", "EnableClickToCallReports"],
    includedInPlans: [],
  },
  // ── Email ─────────────────────────────────────────────────────────────────
  {
    id: "email-suite",
    label: "Email Suite",
    description: "Gmail and Outlook inbound ticketing, email campaigns, AI auto-resolution, AI-composed outbound emails, and Copilot Write.",
    icon: "fa-envelope",
    color: "sky",
    features: ["EnableGmailIntegration", "EnableOutlookIntegration", "EnableEmailCampaigns", "EnableEmailAiResolution", "EnableAiComposedEmails", "EnableCopilotWrite"],
    includedInPlans: ["enterprise"],
  },
  // ── CRM ──────────────────────────────────────────────────────────────────
  {
    id: "crm",
    label: "CRM Suite",
    description: "HubSpot, Dynamics 365, NetSuite, Salesforce, Pipedrive, Zoho, and ServiceNow — contact/account/lead/case AI skills and sidebar panels.",
    icon: "fa-address-book",
    color: "indigo",
    features: ["EnableHubSpot", "EnableHubSpotSkills", "EnableDynamics", "EnableNetsuite", "EnableNetSuiteSkills", "EnableSalesforce", "EnablePipedriveSkills", "EnableZohoSkills", "EnableServiceNow"],
    includedInPlans: ["enterprise"],
  },
  {
    id: "native-crm",
    label: "Native CRM & Intelligence",
    description: "Velaro's built-in CRM — contacts, deals, buying signals, health scores, timeline, e-commerce sync, visitor tracking, and intelligence suite.",
    icon: "fa-star",
    color: "amber",
    features: ["EnableNativeCrm", "EnableBuyingSignals", "EnableCustomerHealthScore", "EnableContactTimeline", "EnableContactManager", "EnableConversationLifecycle", "EnableEcommerceSync", "EnableVisitorTracking", "EnableIntelligenceSuite", "EnableThemeClassification"],
    includedInPlans: ["enterprise"],
  },
  // ── Commerce ─────────────────────────────────────────────────────────────
  {
    id: "commerce",
    label: "Commerce",
    description: "Shopify, BigCommerce, WooCommerce, Magento, Square, Clover, Jobber — product catalog bot indexing and AI skills.",
    icon: "fa-bag-shopping",
    color: "amber",
    features: ["EnableShopify", "EnableShopifyBotIndexing", "EnableBigCommerce", "EnableBigCommerceBotIndexing", "EnableWooCommerce", "EnableMagento", "EnableSquare", "EnableCloverSkills", "EnableJobberSkills"],
    includedInPlans: ["pro", "enterprise"],
  },
  {
    id: "restaurant-booking",
    label: "Restaurant Booking",
    description: "AI phone reservations via OpenTable and Resy — callers check availability, pick a slot, and get confirmed by SMS.",
    icon: "fa-utensils",
    color: "red",
    features: ["EnableOpenTableSkills", "EnableResySkills"],
    includedInPlans: ["enterprise"],
  },
  {
    id: "hospitality",
    label: "Hospitality",
    description: "Guesty, Uplisting, Cloudbeds, and Mindbody — vacation rental, hotel PMS, and fitness booking AI skills.",
    icon: "fa-hotel",
    color: "teal",
    features: ["EnableGuestySkills", "EnableUplistingSkills", "EnableCloudbedsSkills", "EnableMindbodySkills"],
    includedInPlans: ["enterprise"],
  },
  // ── Productivity ─────────────────────────────────────────────────────────
  {
    id: "productivity",
    label: "Productivity & Automation",
    description: "Zapier, Power Automate, Make.com, Teams/Slack notifications, calendar booking, Klaviyo, Formstack, Accela, and Monday.com.",
    icon: "fa-gears",
    color: "slate",
    features: ["EnableZapier", "EnablePowerAutomate", "EnableMake", "EnableTeamsNotifications", "EnableSlackNotifications", "EnableCalendarBooking", "EnableKlaviyo", "EnableFormstack", "EnableAccela", "EnableMondaySkills", "EnableOneDriveAttachments"],
    includedInPlans: ["enterprise"],
  },
  // ── Workforce ─────────────────────────────────────────────────────────────
  {
    id: "workforce",
    label: "Workforce & Operations",
    description: "Workforce management scheduling, self-serve seat management, operational alerts, ops intelligence, and usage monitoring.",
    icon: "fa-users-gear",
    color: "gray",
    features: ["EnableWorkforceManagement", "EnableSelfServeSeats", "EnableOperationalAlerts", "EnableOpsIntelligence", "EnableUsageAlerts", "EnableSmsAlerts"],
    includedInPlans: ["enterprise"],
  },
  // ── Recruiting ────────────────────────────────────────────────────────────
  {
    id: "recruiting-bot",
    label: "Recruiting Bot",
    description: "AI-powered recruiting intake — job listing lookup, application status, HR intake workflows, and EEOC compliance questions.",
    icon: "fa-user-tie",
    color: "blue",
    features: ["EnableRecruitingBot", "EnableRecruitingBotEeoc"],
    includedInPlans: [],
  },
  // ── Outbound ──────────────────────────────────────────────────────────────
  {
    id: "outbound-campaigns",
    label: "Outbound Campaigns",
    description: "Email & SMS campaigns, outbound IVR, smart callback, landing pages, and advanced campaign reports.",
    icon: "fa-bullhorn",
    color: "orange",
    features: ["EnableOutboundCampaigns", "EnableOutboundIvr", "EnableSmartCallback", "EnableAdvancedCampaignReports", "EnableLandingPages"],
    includedInPlans: ["enterprise"],
  },
  {
    id: "proactive-engagement",
    label: "Proactive Engagement",
    description: "Page-aware contextual messaging — trigger chat invitations based on page content, time on page, and visitor intent.",
    icon: "fa-hand-pointer",
    color: "green",
    features: ["EnableProactiveEngagement", "EnablePageWidgets"],
    includedInPlans: ["enterprise"],
  },
  // ── Platform ─────────────────────────────────────────────────────────────
  {
    id: "platform",
    label: "Platform Add-ons",
    description: "Multiple widget deployments, page widgets, and custom portal domain.",
    icon: "fa-layer-group",
    color: "gray",
    features: ["EnableMultipleDeployments", "EnablePageWidgets", "EnablePortalCustomDomain"],
    includedInPlans: ["pro", "enterprise"],
  },
  // ── Security ─────────────────────────────────────────────────────────────
  {
    id: "security-compliance",
    label: "Security & Compliance",
    description: "Enterprise SSO, MFA, passkeys, IP restrictions, session controls, data retention, PII scrubbing, PII redaction, compliance mode, and BYOS transcript archiving.",
    icon: "fa-shield-halved",
    color: "slate",
    features: ["EnableEnterpriseSso", "EnableAdvancedMfa", "EnablePasskeys", "EnableIpBlocking", "EnableAgentIpRestriction", "EnableSessionTimeout", "EnableConcurrentSessionLimit", "EnableDataRetention", "EnableScheduledPurge", "EnableTranscriptStorage", "EnableMessageScrubbing", "EnablePiiRedaction", "EnableComplianceMode"],
    includedInPlans: ["enterprise"],
  },
];
